import siteStaffPanelStyles from './site-staff-panel.styles.css?inline';
import '../../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import { SiteAssignmentsApi } from '../../../api/security/site-assignments-api';
import type {
  LoginUser,
  UserSiteAssignmentDto,
} from '../../../api/agwa-api-model';
import { confirmAction } from '../../../components/dialogs/agwa-confirm-dialog';
import { styles as sharedStyles } from '../../../styles/shared-styles';

import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';

import '../../../components/data-table/data-table';
import type {
  DataTableColumnDef,
  DataTable,
} from '../../../components/data-table/data-table';
import type { DataTableDataProvider } from '../../../components/data-table/data-table-provider';
import { terms } from '../../../language/terms';
import { personName } from '../../../utility/person-name';
import '../../../components/user/username';

/** Staff member row with assignment info */
interface StaffRow {
  userId: string;
  username: string;
  displayName: string;
  emailAddress: string;
  locked: boolean;
  /** Set when assigned to this site */
  assignmentId?: string;
  allSites?: boolean;
}

@customElement('site-staff-panel')
export class SiteStaffPanel extends LitElement {
  static styles = [sharedStyles, unsafeCSS(siteStaffPanelStyles)];

  @property({ type: String }) siteId = '';
  @property({ type: String }) groupId = '';
  @property({ type: String }) siteName = '';

  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private allStaff: LoginUser[] = [];
  @state() private siteAssignments: UserSiteAssignmentDto[] = [];
  @state() private assignedProvider?: DataTableDataProvider<StaffRow>;
  @state() private availableProvider?: DataTableDataProvider<StaffRow>;
  @state() private actionInProgress: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.siteId && this.groupId) {
      void this.loadData();
    }
  }

  updated(changedProps: Map<string, unknown>) {
    if (
      (changedProps.has('siteId') || changedProps.has('groupId')) &&
      this.siteId &&
      this.groupId
    ) {
      void this.loadData();
    }
  }

  private async loadData() {
    this.loading = true;
    this.error = null;

    try {
      // Load both group staff and site assignments in parallel
      const [staffResponse, assignmentsResponse] = await Promise.all([
        ApiFactory.authenticated(ApiUserMaintenance).getGroupUsers({
          loginGroupId: this.groupId,
          includeSecurityContexts: false,
        }),
        ApiFactory.authenticated(SiteAssignmentsApi).getSiteUsers(this.siteId),
      ]);

      if (!staffResponse.success || !staffResponse.data?.users) {
        throw new Error(
          staffResponse.error ||
            `Unable to load ${terms.staffUser.lowerPlural}.`
        );
      }

      this.allStaff = staffResponse.data.users;
      this.siteAssignments = assignmentsResponse.success
        ? (assignmentsResponse.data?.assignments ?? [])
        : [];

      this.buildProviders();
    } catch (e) {
      this.error = (e as Error)?.message ?? 'Unable to load data.';
    } finally {
      this.loading = false;
    }
  }

  private buildProviders() {
    const assignmentMap = new Map(
      this.siteAssignments.map((a) => [a.loginUserId, a])
    );

    const assignedRows: StaffRow[] = [];
    const availableRows: StaffRow[] = [];

    for (const user of this.allStaff) {
      const assignment = assignmentMap.get(user.id);
      const row: StaffRow = {
        userId: user.id,
        username: user.username ?? '',
        displayName: user.displayName ?? '',
        emailAddress: user.emailAddress ?? '',
        locked: user.locked ?? false,
        assignmentId: assignment?.id,
        allSites: assignment?.allSites,
      };

      if (assignment) {
        assignedRows.push(row);
      } else {
        availableRows.push(row);
      }
    }

    this.assignedProvider = async () => ({
      items: assignedRows,
      totalCount: assignedRows.length,
      hasMore: false,
    });

    this.availableProvider = async () => ({
      items: availableRows,
      totalCount: availableRows.length,
      hasMore: false,
    });
  }

  private get assignedColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Username',
        fieldName: 'username',
        width: '180px',
        sortable: true,
      },
      {
        title: 'Display Name',
        fieldName: 'displayName',
        width: '200px',
        // The person named by id through the directory (D-151, contact rule 8); the Username
        // column beside it stays, as the account's login identifier being administered.
        displayValue: (_table: DataTable, item: StaffRow) =>
          html`<user-name .userId=${item.userId}></user-name>`,
      },
      {
        title: 'Email',
        fieldName: 'emailAddress',
        width: '220px',
      },
      {
        title: 'State',
        fieldName: 'locked',
        width: '90px',
        displayValue: (_table: DataTable, item: StaffRow) =>
          html`<agwa-state-badge
            domain="staffAccess"
            state=${item.locked ? 'Blocked' : 'Active'}
          ></agwa-state-badge>`,
      },
      {
        title: 'Access',
        fieldName: 'allSites',
        width: '100px',
        displayValue: (_table: DataTable, item: StaffRow) =>
          item.allSites
            ? html`<sl-tag size="small" variant="primary"
                >All ${terms.loginSite.plural}</sl-tag
              >`
            : '',
      },
      {
        title: '',
        fieldName: '_remove',
        width: '100px',
        fixed: 'right',
        displayValue: (_table: DataTable, item: StaffRow) => html`
          <sl-button
            size="small"
            variant="danger"
            outline
            ?loading=${this.actionInProgress === item.userId}
            ?disabled=${this.actionInProgress === item.userId}
            @click=${() => this.handleRemove(item)}
          >
            <sl-icon slot="prefix" name="x"></sl-icon>
            Remove
          </sl-button>
        `,
      },
    ];
  }

  private get availableColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Username',
        fieldName: 'username',
        width: '180px',
        sortable: true,
      },
      {
        title: 'Display Name',
        fieldName: 'displayName',
        width: '200px',
        // The person named by id through the directory (D-151, contact rule 8); the Username
        // column beside it stays, as the account's login identifier being administered.
        displayValue: (_table: DataTable, item: StaffRow) =>
          html`<user-name .userId=${item.userId}></user-name>`,
      },
      {
        title: 'Email',
        fieldName: 'emailAddress',
        width: '220px',
      },
      {
        title: 'State',
        fieldName: 'locked',
        width: '90px',
        displayValue: (_table: DataTable, item: StaffRow) =>
          html`<agwa-state-badge
            domain="staffAccess"
            state=${item.locked ? 'Blocked' : 'Active'}
          ></agwa-state-badge>`,
      },
      {
        title: '',
        fieldName: '_add',
        width: '100px',
        fixed: 'right',
        displayValue: (_table: DataTable, item: StaffRow) => html`
          <sl-button
            size="small"
            variant="primary"
            outline
            ?loading=${this.actionInProgress === item.userId}
            ?disabled=${this.actionInProgress === item.userId}
            @click=${() => this.handleAdd(item)}
          >
            <sl-icon slot="prefix" name="plus"></sl-icon>
            Add
          </sl-button>
        `,
      },
    ];
  }

  private async handleAdd(item: StaffRow) {
    const confirmed = await confirmAction({
      title: `Add ${terms.staffUser.plural} to ${terms.loginSite.value}`,
      message: `Add "${personName(item.displayName)}" to ${this.siteName || `this ${terms.loginSite.lower}`}?`,
      confirmText: 'Add',
      cancelText: 'Cancel',
      variant: 'primary',
    });

    if (!confirmed) return;

    this.actionInProgress = item.userId;
    try {
      const api = ApiFactory.authenticated(SiteAssignmentsApi);
      const response = await api.assignUserToSite({
        loginUserId: item.userId,
        loginSiteId: this.siteId,
        allSites: false,
      });

      if (!response.success) {
        throw new Error(
          response.error ||
            `Failed to add ${terms.staffUser.lowerPlural} to ${terms.loginSite.lower}.`
        );
      }

      await this.loadData();
    } catch (e) {
      this.error =
        (e as Error)?.message ??
        `Failed to add ${terms.staffUser.lowerPlural} to ${terms.loginSite.lower}.`;
    } finally {
      this.actionInProgress = null;
    }
  }

  private async handleRemove(item: StaffRow) {
    if (!item.assignmentId) return;

    const confirmed = await confirmAction({
      title: `Remove ${terms.staffUser.plural} from ${terms.loginSite.value}`,
      message: `Remove "${personName(item.displayName)}" from ${this.siteName || `this ${terms.loginSite.lower}`}?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) return;

    this.actionInProgress = item.userId;
    try {
      const api = ApiFactory.authenticated(SiteAssignmentsApi);
      const response = await api.removeAssignment(item.assignmentId);

      if (!response.success) {
        throw new Error(
          response.error ||
            `Failed to remove ${terms.staffUser.lowerPlural} from ${terms.loginSite.lower}.`
        );
      }

      await this.loadData();
    } catch (e) {
      this.error =
        (e as Error)?.message ??
        `Failed to remove ${terms.staffUser.lowerPlural} from ${terms.loginSite.lower}.`;
    } finally {
      this.actionInProgress = null;
    }
  }

  private get assignedCount(): number {
    return this.siteAssignments.length;
  }

  private get availableCount(): number {
    const assignedIds = new Set(this.siteAssignments.map((a) => a.loginUserId));
    return this.allStaff.filter((u) => !assignedIds.has(u.id)).length;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-container">
          <sl-spinner></sl-spinner>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.error}
        </sl-alert>
      `;
    }

    return html`
      ${this.renderAssignedSection()} ${this.renderAvailableSection()}
    `;
  }

  private renderAssignedSection() {
    return html`
      <div class="section">
        <div class="section-header">
          <h3 class="section-title">Assigned ${terms.staffUser.value}</h3>
          <span class="count-badge">${this.assignedCount}</span>
        </div>
        ${
          this.assignedProvider
            ? html`
                <agwa-lit-table
                  fill-height
                  .columns=${this.assignedColumns}
                  .dataProvider=${this.assignedProvider}
                  .hidePagination=${true}
                ></agwa-lit-table>
              `
            : html`<div class="empty-state">
                No ${terms.staffUser.lowerPlural} assigned to this
                ${terms.loginSite.lower}.
              </div>`
        }
      </div>
    `;
  }

  private renderAvailableSection() {
    return html`
      <div class="section">
        <div class="section-header">
          <h3 class="section-title">Available ${terms.staffUser.value}</h3>
          <span class="count-badge">${this.availableCount}</span>
        </div>
        ${
          this.availableProvider
            ? html`
                <agwa-lit-table
                  fill-height
                  .columns=${this.availableColumns}
                  .dataProvider=${this.availableProvider}
                  .hidePagination=${true}
                ></agwa-lit-table>
              `
            : html`
                <div class="empty-state">
                  All ${terms.staffUser.lowerPlural} are assigned to this
                  ${terms.loginSite.lower}.
                </div>
              `
        }
      </div>
    `;
  }
}
